import React, { FC, useState, useMemo, Fragment } from 'react';
import { Modal, DatePicker, InputNumber, Input, App, Form, Button, Flex } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { PurchaseOrderItem } from '@/types/purchase';
import SText from '@/styles/SText';
import { OrderAddItemV2, OrderAddV2, OrderItemV2ExtAdd } from '@/types/inventoryV2';
import { ChangeEnum, OperateEnum } from '@/types/inventory';
import { SourceTypeEnum } from '@/types/common';
import EditExt from './EditExt';
import { EditOutlined } from '@ant-design/icons';
import { generateLotV2 } from '@/utils/codeParse';
import CommonTable from "@/components/CommonTable";

interface MultiInventoryProps {
  open: boolean;
  selectedRows: PurchaseOrderItem[];
  onCancel: () => void;
  onOk: (data: OrderAddV2) => void;
  loading?: boolean;
}

interface TableDataType extends PurchaseOrderItem {
  supplierLot?: string;
  lot: string;
  deliveryCount?: number;
  maxQty?: number;
  ext?: OrderItemV2ExtAdd;
}

const MultiInventory: FC<MultiInventoryProps> = ({
  open,
  selectedRows,
  onCancel,
  onOk,
  loading = false
}) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const [deliveryTime, setDeliveryTime] = useState<Dayjs | null>(dayjs());
  const [tableData, setTableData] = useState<TableDataType[]>([]);

  const [editExtOpen, setEditExtOpen] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState<string | null>(null);
  const [desc, setDesc] = useState<string>('');

  React.useEffect(() => {
    if (open && selectedRows.length > 0) {
      const initialData = selectedRows.map(item => {
        const totalInventoryCount = item.inventoryItems?.reduce((sum, inv) => sum + (inv.count || 0), 0) || 0;
        const maxQty = item.count + item.spareCount - totalInventoryCount;
        return {
          ...item,
          supplierLot: '',
          deliveryCount: maxQty,
          maxQty: maxQty,
          ext: undefined,
          lot: '',
        };
      });
      genAllLot(initialData);
    }
  }, [open, selectedRows]);

  const handleSupplierLotChange = (value: string, record: TableDataType) => {
    const newData = tableData.map(item =>
      item.id === record.id ? { ...item, supplierLot: value } : item
    );
    setTableData(newData);
  };

  const handleLotChange = (value: string, record: TableDataType) => {
    const newData = tableData.map(item =>
      item.id === record.id ? { ...item, lot: value } : item
    );
    setTableData(newData);
  };

  const handleDeliveryCountChange = (value: number | null, record: TableDataType) => {
    const newData = tableData.map(item =>
      item.id === record.id ? { ...item, deliveryCount: value || 0 } : item
    );
    setTableData(newData);
  };

  const handleOpenEditExt = (record: TableDataType) => {
    setEditingRecordId(record.id);
    setEditExtOpen(true);
  };

  const handleEditExtConfirm = (ext?: OrderItemV2ExtAdd) => {
    // 更新当前编辑行的 ext 数据
    if (editingRecordId) {
      setTableData(prev => prev.map(item =>
        item.id === editingRecordId ? { ...item, ext } : item
      ));
    }
    setEditExtOpen(false);
    setEditingRecordId(null);
  };

  const handleEditExtClose = () => {
    setEditExtOpen(false);
    setEditingRecordId(null);
  };

  const getEditingRecordExt = (): OrderItemV2ExtAdd | undefined => {
    if (!editingRecordId) return undefined;
    return tableData.find(item => item.id === editingRecordId)?.ext;
  };

  const handleGenLot = (record: TableDataType) => {
    let lotStr = generateLotV2(record?.product?.code ?? '', record?.order?.supplier?.code ?? '', Date.now());
    if (!!!lotStr) {
      notification.error({ message: t('common.lotGetErr'), description: `${t('purchase.printCode')}：${record?.product?.code ?? ''}，${t('purchase.code')}：${record?.order?.supplier?.code ?? ''}` })
      return
    }
    handleLotChange(lotStr, record);
  };

  const columns: ColumnsType<TableDataType> = [
    {
      title: <SText text={t('purchase.orderNumber')} />,
      dataIndex: 'number',
      key: 'number',
      width: 180,
      fixed: "left",
      render: (_, item) => <SText text={item?.number} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('purchase.inventoryCode')} />,
      dataIndex: ['product', 'code'],
      key: 'productCode',
      width: 120,
      render: (_, item) => <SText text={item?.product?.code ?? ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('purchase.supplierCode')} />,
      dataIndex: ['order', 'supplier', 'code'],
      key: 'supplierCode',
      width: 120,
      render: (_, item) => <SText text={item?.order?.supplier?.code} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('purchase.supplierName')} />,
      dataIndex: ['order', 'supplier', 'name'],
      key: 'supplierName',
      width: 150,
      render: (_, item) => <SText text={item?.order?.supplier?.name ?? ""} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('counting.lotStr')} />,
      dataIndex: 'lot',
      key: 'lot',
      width: 200,
      render: (_, record) => (
        <Input
          value={record.lot}
          onChange={e => handleLotChange(e.target.value, record)}
          placeholder={t('counting.lotStr')}
          suffix={
            <Button onClick={() => handleGenLot(record)} >{t('common.genLot')}</Button>
          }
        />
      )
    },
    {
      title: <SText text={t('purchase.purchaseQuantity')} />,
      dataIndex: 'count',
      key: 'count',
      width: 100,
      render: (_, item) => <SText text={item?.count?.toString() ?? ""} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('purchase.spareRate')} />,
      dataIndex: 'spareRate',
      key: 'spareRate',
      width: 80,
      render: (_, item) => <SText text={Number(item?.spareRate ?? "") + ''} />
    },
    {
      title: <SText text={t('purchase.spareCount')} />,
      dataIndex: 'spareCount',
      key: 'spareCount',
      width: 80,
      render: (_, item) => <SText text={item.spareCount + ''} />
    },
    {
      title: <SText text={t('purchase.supplierLot')} />,
      dataIndex: 'supplierLot',
      key: 'supplierLot',
      width: 150,
      render: (_, record) => (
        <Input
          disabled={(record.maxQty || 0) <= 0}
          value={record.supplierLot}
          onChange={e => handleSupplierLotChange(e.target.value, record)}
          placeholder={t('purchase.plSupplierLot')}
        />
      )
    },
    {
      title: <SText text={t('purchase.maxDeliveryQty')} />,
      dataIndex: 'maxQty',
      key: 'maxQty',
      width: 100,
      fixed: "right",
      render: (_, item) => <SText text={item.maxQty?.toString() ?? "0"} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('purchase.quantity')} />,
      dataIndex: 'deliveryCount',
      key: 'deliveryCount',
      width: 120,
      fixed: "right",
      render: (_, record) => {
        const maxCount = record.maxQty || 0;
        return (
          <InputNumber changeOnWheel
            disabled={maxCount <= 0}
            min={0}
            max={maxCount}
            value={record.deliveryCount}
            onChange={value => handleDeliveryCountChange(value, record)}
            placeholder={`0-${maxCount}`}
            style={{ width: '100%' }}
          />
        );
      }
    },
    {
      title: t('common.opt'),
      key: 'operation',
      width: 120,
      fixed: "right",
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EditOutlined />}
          onClick={() => handleOpenEditExt(record)}
          title={t('purchase.otherInfo')}
          disabled={!((record?.deliveryCount || 0) > 0)}
        >
          {t('purchase.otherInfo')}
        </Button>
      )
    }
  ];

  const handleOk = () => {
    if (!deliveryTime) {
      notification.error({ placement: 'top', message: t('purchase.pleaseSelectDeliveryTime') });
      return;
    }

    let validItems = tableData.filter(item => item.deliveryCount && item.deliveryCount > 0 && !!item.lot);
    if (validItems.length === 0) {
      notification.error({ placement: 'top', message: t('purchase.pleaseInputDeliveryCount'), description: t('purchase.leastOneMoreZero') });
      return;
    }

    // 生成统一的批次号（使用第一个订单号+时间戳）
    // const lotStr = validItems[0].number + (Date.now() + '');

    // 构建单个OrderAddV2数据，包含多个items
    let now = Date.now();
    let items: OrderAddItemV2[] = [];
    for (let i = 0; i < validItems.length; i++) {
      const item = validItems[i];
      let lotStr = generateLotV2(item?.product?.code ?? '', item?.order?.supplier?.code ?? '', ++now);
      if (!!!lotStr) {
        notification.error({ message: t('common.lotGetErr'), description: `${t('purchase.printCode')}：${item?.product?.code ?? ''}，${t('purchase.code')}：${item?.order?.supplier?.code ?? ''}` })
        return
      }
      items.push({
        sourceType: SourceTypeEnum.PURCHASE_ITEM,
        sourceId: item.id,
        productId: item.product?.id ?? '',
        count: item.deliveryCount!,
        lot: lotStr,
        thdlot: item.supplierLot!,
        desc: '',
        locations: [],
        ext: item.ext
      })
    }
    const orderAddData: OrderAddV2 = {
      type: ChangeEnum.PRE_IN,
      opType: OperateEnum.TRANSACTION,
      desc,
      preTime: deliveryTime ? deliveryTime.endOf('day').valueOf() : null,
      arrivalAt: null,
      items,
    };

    onOk(orderAddData);
  };


  const genAllLot = (rows?: TableDataType[]) => {
    let resData: TableDataType[] = [...(rows || tableData)];
    let now = Date.now();
    for (let i = 0; i < resData.length; i++) {
      let lot: string | undefined = undefined;
      if (resData[i].product?.code) lot = generateLotV2(resData[i].product?.code ?? '', '', ++now);
      if (!!!resData[i].lot) resData[i].lot = lot!
    };
    setTableData([...resData]);
  };
  return (
    <>
      <Modal
        title={t('purchase.batchDeliveryNote')}
        open={open}
        onCancel={onCancel}
        onOk={handleOk}
        width={1200}
        confirmLoading={loading}
        destroyOnClose
        maskClosable={false}
        okButtonProps={{ disabled: tableData.length === 0 }}
      >
        <div style={{ marginBottom: 6 }}>
          <span style={{ marginRight: 8 }}><span style={{ color: "red" }}>* </span>{t('purchase.deliveryDate')}:</span>
          <DatePicker
            value={deliveryTime}
            onChange={setDeliveryTime}
            style={{ width: 200 }}
            format="YYYY-MM-DD"
            allowClear={false}
            placeholder={t("purchase.selectDeliveryDate")}
          />
        </div>
        <Form.Item label={t('requirement.inOrderDesc')} style={{ marginBottom: 6 }}>
          <Input.TextArea size="small" autoSize={{ minRows: 1, maxRows: 1 }} maxLength={200} value={desc} onChange={e => setDesc(e.target.value)} placeholder={t('requirement.plInventoryDesc')} />
        </Form.Item>
        {tableData.length > 0 ? (
          <Fragment>
            <Flex style={{ marginBottom: 6 }}>
              <Button onClick={() => genAllLot()} type='dashed'>
                {t('common.genAllLot')}
              </Button>
            </Flex>
            <CommonTable
              columns={columns}
              dataSource={tableData}
              rowKey="id"
              virtual
              pagination={false}
              bordered={false}
              scroll={{ y: 400 }}
              size="small"
            />
          </Fragment>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
            {t('purchase.noDeliveryData')}
          </div>
        )}
      </Modal>
      <EditExt
        open={editExtOpen}
        data={getEditingRecordExt()}
        onConfirm={handleEditExtConfirm}
        onClose={handleEditExtClose}
      />
    </>
  );
};

export default MultiInventory;
